import { useImportDuoExamResultsMutation } from "@/api/mutations/duo/useImportDuoExamResultsMutation"
import { useDuoExamResultImportsInfiniteQuery } from "@/api/queries/duo/useDuoExamResultImportsInfiniteQuery"
import { DuoSidebarActionButton } from "@/components/duo/DuoSidebarActionButton"
import { DuoWorkspaceLayout } from "@/components/duo/DuoWorkspaceLayout"
import { ExamResultsImportDetail } from "@/components/duo/ExamResultsImportDetail"
import {
  DUO_IMPORT_ACCEPT,
  DUO_IMPORT_MAX_FILE_SIZE,
} from "@/components/duo/duoImportFileConfig"
import { getDuoExamImportErrorMessage } from "@/components/duo/examResultsImportUtils"
import { checkPermissions } from "@/utils/checkPermissions"
import { ChevronRightIcon } from "@heroicons/react/24/outline"
import { createFileRoute, useNavigate, useSearch } from "@tanstack/react-router"
import { useState } from "react"
import {
  Button,
  DropZone,
  PageHeader,
  Skeleton,
  cn,
  formatDateToDutch,
} from "toptaal-shared"
import { z } from "zod"

const searchSchema = z.object({
  importId: z.number().optional(),
})

export const Route = createFileRoute("/duo/examenresultaten/")({
  component: RouteComponent,
  validateSearch: searchSchema,
  beforeLoad: () => checkPermissions("read", "duo"),
})

function RouteComponent() {
  const { importId } = useSearch({ from: "/duo/examenresultaten/" })
  const navigate = useNavigate({ from: "/duo/examenresultaten/" })
  const {
    data: historyData,
    isLoading: isHistoryLoading,
    isError: isHistoryError,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useDuoExamResultImportsInfiniteQuery()
  const imports = historyData?.imports ?? []

  const selectImport = (selectedImportId: number) => {
    navigate({ search: { importId: selectedImportId }, replace: true })
  }

  const openImportForm = () => {
    navigate({ search: {}, replace: true })
  }

  return (
    <DuoWorkspaceLayout
      sidebar={
        <>
          <PageHeader>DUO examenresultaten</PageHeader>

          <DuoSidebarActionButton
            aria-current={importId === undefined ? "page" : undefined}
            onClick={openImportForm}
          >
            <span>
              Nieuwe <strong>examenresultaten</strong>
            </span>
          </DuoSidebarActionButton>

          <div className="mt-3 border-t border-primary-accent-blue pt-3">
            {isHistoryLoading && <ImportHistorySkeleton />}

            {isHistoryError && (
              <p className="text-sm text-red" role="alert">
                De importgeschiedenis kon niet worden geladen.
              </p>
            )}

            {!isHistoryLoading && !isHistoryError && imports.length === 0 && (
              <p className="py-3 text-sm text-primary-dark-blue/70">
                Er zijn nog geen examenresultaten geïmporteerd.
              </p>
            )}

            <div className="flex flex-col gap-2">
              {imports.map((examResultImport) => {
                const isSelected = importId === examResultImport.id

                return (
                  <button
                    key={examResultImport.id}
                    type="button"
                    aria-current={isSelected ? "page" : undefined}
                    className={cn(
                      "flex h-11 w-full items-center justify-between rounded px-4 text-left transition-colors",
                      isSelected
                        ? "bg-primary-green text-white"
                        : "bg-light-grey hover:bg-primary-light-blue",
                    )}
                    onClick={() => selectImport(examResultImport.id)}
                  >
                    <span>
                      {formatDateToDutch(examResultImport.insertedAt)}
                    </span>
                    <ChevronRightIcon className="size-5" />
                  </button>
                )
              })}

              {hasNextPage && (
                <Button
                  type="button"
                  variant="secondary"
                  className="mt-1 w-full"
                  isLoading={isFetchingNextPage}
                  onClick={() => fetchNextPage()}
                >
                  Meer laden
                </Button>
              )}
            </div>
          </div>
        </>
      }
    >
      {importId === undefined ? (
        <ExamResultsImportForm
          onImported={(importedId) =>
            navigate({
              search: { importId: importedId },
              replace: true,
            })
          }
        />
      ) : (
        <ExamResultsImportDetail importId={importId} />
      )}
    </DuoWorkspaceLayout>
  )
}

function ExamResultsImportForm({
  onImported,
}: {
  onImported: (importId: number) => void
}) {
  const [files, setFiles] = useState<File[]>([])
  const [dropzoneKey, setDropzoneKey] = useState(0)
  const mutation = useImportDuoExamResultsMutation()
  const errorMessage = getDuoExamImportErrorMessage(mutation.error)

  const importFile = () => {
    const [file] = files
    if (!file) return

    mutation.mutate(file, {
      onSuccess: (response) => {
        setFiles([])
        setDropzoneKey((key) => key + 1)
        onImported(response.data.id)
      },
    })
  }

  return (
    <div className="mx-auto max-w-2xl pt-36">
      <p className="mb-5 text-center font-bold text-primary-dark-blue">
        Importeer examenresultaten van DUO in MTT
      </p>

      <DropZone
        key={dropzoneKey}
        variant="white"
        accept={DUO_IMPORT_ACCEPT}
        maxFileSize={DUO_IMPORT_MAX_FILE_SIZE}
        selectedDocumentType="other"
        handleAddFiles={(newFiles) => setFiles(newFiles.slice(-1))}
        handleRemoveFile={() => setFiles([])}
      />

      {mutation.isError && errorMessage && (
        <p
          className="mt-4 rounded border border-red/30 bg-light-red p-3 text-sm text-red"
          role="alert"
        >
          {errorMessage}
        </p>
      )}

      <div className="mt-6 flex justify-center">
        <Button
          type="button"
          isLoading={mutation.isPending}
          disabled={files.length === 0}
          onClick={importFile}
        >
          Importeren
        </Button>
      </div>
    </div>
  )
}

function ImportHistorySkeleton() {
  return (
    <div className="flex flex-col gap-2" aria-label="Importgeschiedenis laden">
      {Array.from({ length: 5 }, (_, index) => (
        <Skeleton key={index} className="h-11 w-full" />
      ))}
    </div>
  )
}
